import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  ImagePlus,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import api from "@/utils/axios-utils";
import LoadingSpinner from "@/components/LoadingSpinner";
import PrimaryButton from "@/components/PrimaryButton";
import SecondaryButton from "@/components/SecondaryButton";
import Modal from "@/components/Modal";
import { useCurriculumManagementEnabled } from "@/hooks/useRuntimeFlags";
import { useMyTrial } from "@/hooks/useMyTrial";
import { isTrialLocked } from "@/components/TrialBanner";
import { useProfile } from "@/hooks/useProfile";
import {
  EditorLesson,
  TrashAction,
  payloadLessons,
  trashAction,
  trashLessonSlot,
} from "./curriculumSlots";

type Layer = { depth: number; name: string };
// Served by the backend rather than listed here, so the editor cannot offer a
// grade name that `parse_grade` will not read. Optional because an older
// backend omits it, in which case the name falls back to a text field.
type GradeOption = { code: string; label: string };
type LessonOption = { id: number; name: string; is_published?: boolean };
type EditorNode = {
  id: number;
  parent_id: number | null;
  name: string;
  sort_order: number;
  children: EditorNode[];
  lessons: EditorLesson[];
};
type CurriculumSummary = {
  id: number;
  name: string;
  logo_url: string | null;
  organization_count: number;
  layer_count: number;
  lesson_count: number;
  placeholder_count: number;
  revision: number;
  updated_at: string;
  can_edit: boolean;
};
type CurriculumDetail = CurriculumSummary & {
  created_by_user_id: number | null;
  layers: Layer[];
  organizations: { id: string; name: string | null }[];
  nodes: {
    id: number;
    parent_id: number | null;
    depth: number;
    name: string;
    sort_order: number;
    is_lesson: boolean;
    lessons: {
      id: number;
      node_id: number;
      lesson_id: number | null;
      title: string;
      status: string;
      ever_published?: boolean;
    }[];
  }[];
  grade_options?: GradeOption[];
};

// Positions are fixed, so a slot that held a published lesson is emptied, not
// removed: students keep walking past it.
const TRASH_HINTS: Record<TrashAction, string | undefined> = {
  delete: undefined,
  empty: "Remove the lesson. The slot stays, empty, in its place.",
  kept: "This slot held a published lesson, so it stays as an empty slot.",
};

let nextTempId = -1;
const tempId = () => nextTempId--;

/** One entry from the removal guard's 422, describing what a save would drop. */
type RemovalNode = {
  id: number;
  name: string | null;
  published: boolean;
  scheduled: boolean;
};

const emptyLayers = (): Layer[] => [
  { depth: 0, name: "Grade" },
  { depth: 1, name: "Lesson" },
];

const fromDetail = (detail: CurriculumDetail): EditorNode[] => {
  const nodes = new Map<number, EditorNode>();
  detail.nodes
    .filter((node) => !node.is_lesson)
    .forEach((node) => {
      nodes.set(node.id, {
        id: node.id,
        parent_id: node.parent_id,
        name: node.name,
        sort_order: node.sort_order,
        children: [],
        lessons: [],
      });
    });
  detail.nodes
    .filter((node) => node.is_lesson)
    .forEach((node) => {
      const parent = nodes.get(node.parent_id ?? -1);
      const lesson = node.lessons[0];
      if (parent && lesson) {
        parent.lessons.push({
          id: node.id,
          title: lesson.title,
          lesson_id: lesson.lesson_id,
          ever_published: lesson.ever_published,
        });
      }
    });
  nodes.forEach((node) => {
    if (node.parent_id !== null) {
      nodes.get(node.parent_id)?.children.push(node);
    }
  });
  return [...nodes.values()]
    .filter((node) => node.parent_id === null)
    .sort((a, b) => a.sort_order - b.sort_order);
};

const updateNode = (
  nodes: EditorNode[],
  id: number,
  updater: (node: EditorNode) => EditorNode,
): EditorNode[] =>
  nodes.map((node) => {
    if (node.id === id) return updater(node);
    return {
      ...node,
      children: updateNode(node.children, id, updater),
    };
  });

const removeNode = (nodes: EditorNode[], id: number): EditorNode[] =>
  nodes
    .filter((node) => node.id !== id)
    .map((node) => ({ ...node, children: removeNode(node.children, id) }));

const toPayloadNodes = (nodes: EditorNode[]) =>
  nodes.map((node, index) => ({
    id: node.id,
    parent_id: node.parent_id,
    name: node.name,
    sort_order: node.sort_order || index,
    children: toPayloadNodes(node.children),
    lessons: payloadLessons(node.lessons),
  }));

const CurriculumManagementPage = () => {
  const navigate = useNavigate();
  const { curriculumId } = useParams();
  const { trial } = useMyTrial();
  const locked = trial ? isTrialLocked(trial) : false;
  const curriculumManagementEnabled = useCurriculumManagementEnabled();
  const { profile } = useProfile(curriculumManagementEnabled);
  const [curricula, setCurricula] = useState<CurriculumSummary[]>([]);
  const [detail, setDetail] = useState<CurriculumDetail | null>(null);
  const [layers, setLayers] = useState<Layer[]>(emptyLayers);
  const [nodes, setNodes] = useState<EditorNode[]>([]);
  const [lessons, setLessons] = useState<LessonOption[]>([]);
  const [search, setSearch] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<RemovalNode[] | null>(
    null,
  );
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);

  const readOnly =
    locked || detail?.can_edit === false || !curriculumManagementEnabled;
  const gradeOptions = detail?.grade_options ?? [];
  const visibleCurricula = useMemo(
    () =>
      curricula.filter((item) =>
        item.name.toLowerCase().includes(search.toLowerCase()),
      ),
    [curricula, search],
  );

  const loadCurricula = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<CurriculumSummary[]>("/curriculum");
      setCurricula(response.data);
    } catch {
      setError("Unable to load curricula. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const [detailResponse, lessonsResponse] = await Promise.all([
        api.get<CurriculumDetail>(`/curriculum/${id}`),
        // Drafts too: a slot is normally filled while its lesson is still a
        // draft, and the sequence schedules it in place.
        api.get<LessonOption[]>("/lessons/?include_drafts=true"),
      ]);
      setDetail(detailResponse.data);
      setName(detailResponse.data.name);
      setLayers(
        detailResponse.data.layers.length
          ? detailResponse.data.layers
          : emptyLayers(),
      );
      setNodes(fromDetail(detailResponse.data));
      setLessons(lessonsResponse.data);
      setSelectedOrg(detailResponse.data.organizations[0]?.id ?? null);
    } catch {
      setError("Unable to load curriculum. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!curriculumId) {
      setDetail(null);
      loadCurricula();
    } else {
      loadDetail(curriculumId);
    }
  }, [curriculumId, loadCurricula, loadDetail]);

  const createCurriculum = async () => {
    if (!name.trim() || readOnly) return;
    setSaving(true);
    try {
      const response = await api.post<CurriculumDetail>("/curriculum", {
        name: name.trim(),
      });
      setCreateOpen(false);
      setName("");
      navigate(`/curriculum/${response.data.id}`);
    } catch {
      setError("Unable to create curriculum.");
    } finally {
      setSaving(false);
    }
  };

  const save = async (confirmRemovedNodeIds: number[] = []) => {
    if (!detail || readOnly) return;
    setSaving(true);
    setError(null);
    try {
      // The metadata PATCH bumps the revision and commits, so the structure PUT
      // has to send back what the PATCH returned rather than a number derived
      // from local state. Deriving it meant any rejected PUT -- a layer-shape
      // 422, or the removal guard below -- left this component one revision
      // behind the server, and every later Save 409'd until a full reload.
      const patched = await api.patch<CurriculumDetail>(
        `/curriculum/${detail.id}`,
        { name: name.trim() },
      );
      setDetail(patched.data);
      const response = await api.put<CurriculumDetail>(
        `/curriculum/${detail.id}/structure`,
        {
          revision: patched.data.revision,
          layers,
          nodes: toPayloadNodes(nodes),
          confirm_removed_node_ids: confirmRemovedNodeIds,
        },
      );
      setPendingRemoval(null);
      setDetail(response.data);
      setLayers(response.data.layers);
      setNodes(fromDetail(response.data));
      setName(response.data.name);
    } catch (err: any) {
      const detailPayload = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        setError("Curriculum changed elsewhere. Reload before saving.");
      } else if (
        detailPayload?.code === "curriculum_removals_need_confirmation"
      ) {
        // The save dropped published or already-assigned lessons. Name them and
        // let the editor resend with the removals acknowledged.
        setPendingRemoval(detailPayload.nodes ?? []);
      } else {
        // A FastAPI detail is not always a string, and rendering an object as a
        // React child throws.
        setError(
          typeof detailPayload === "string"
            ? detailPayload
            : (detailPayload?.message ?? "Unable to save curriculum."),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const addGrade = () => {
    setNodes((current) => [
      ...current,
      {
        id: tempId(),
        parent_id: null,
        // The first grade not already used, because a depth-0 name has to be
        // one of the thirteen: "New grade" is rejected by the save endpoint.
        name:
          gradeOptions.find(
            (option) => !current.some((node) => node.name === option.label),
          )?.label ??
          gradeOptions[0]?.label ??
          "Kindergarten",
        sort_order: current.length,
        children: [],
        lessons: [{ id: tempId(), title: "New lesson", lesson_id: null }],
      },
    ]);
  };

  const addChild = (parent: EditorNode) => {
    const child: EditorNode = {
      id: tempId(),
      parent_id: parent.id,
      name: "New section",
      sort_order: parent.children.length,
      children: [],
      lessons: [],
    };
    setNodes((current) =>
      updateNode(current, parent.id, (node) => ({
        ...node,
        children: [...node.children, child],
      })),
    );
  };

  const addLesson = (parent: EditorNode) => {
    setNodes((current) =>
      updateNode(current, parent.id, (node) => ({
        ...node,
        lessons: [
          ...node.lessons,
          { id: tempId(), title: "New lesson", lesson_id: null },
        ],
      })),
    );
  };

  if (loading) return <LoadingSpinner message="Loading curriculum..." />;

  if (!curriculumId) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-libre-baskerville text-[32px] font-normal leading-[1.45] text-[#294B39]">
              Curriculum
            </h1>
            <p className="mt-1 text-[#687368]">
              Build reusable lesson sequences for your organization.
            </p>
          </div>
          <PrimaryButton
            onClick={() => setCreateOpen(true)}
            disabled={readOnly}
          >
            <Plus className="mr-2 inline h-4 w-4" /> Add curriculum
          </PrimaryButton>
        </div>
        {!curriculumManagementEnabled && (
          <div
            role="status"
            className="rounded-[6px] border border-[#DED8CC] bg-[#EDE8DC] p-4 text-[#294B39]"
          >
            Curriculum authoring is disabled for this workspace. You can review
            existing curriculum, but cannot make changes.
          </div>
        )}
        {locked && (
          <div
            role="status"
            className="rounded-[6px] bg-amber-50 p-4 text-amber-900"
          >
            Your trial is locked. Curriculum editing is read-only.
          </div>
        )}
        {error && (
          <div
            role="alert"
            className="rounded-[6px] bg-red-50 p-4 text-red-800"
          >
            {error}{" "}
            <button className="ml-2 underline" onClick={loadCurricula}>
              Retry
            </button>
          </div>
        )}
        <label className="sr-only" htmlFor="curriculum-search">
          Search curricula
        </label>
        <input
          id="curriculum-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search curricula"
          className="rounded-[6px] border border-[#DED8CC] bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-[#28543D]"
        />
        {visibleCurricula.length === 0 ? (
          <div
            role="status"
            className="rounded-[8px] border border-dashed border-[#DED8CC] bg-white p-12 text-center text-[#687368]"
          >
            No curricula found.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {visibleCurricula.map((curriculum) => (
              <button
                key={curriculum.id}
                onClick={() => navigate(`/curriculum/${curriculum.id}`)}
                className="rounded-[8px] border border-[#DED8CC] bg-white p-5 text-left transition hover:border-[#9AA884] focus:outline-none focus:ring-2 focus:ring-[#28543D]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-[#294B39]">
                      {curriculum.name}
                    </h2>
                    <p className="mt-1 text-sm text-[#687368]">
                      {curriculum.organization_count} organizations
                    </p>
                  </div>
                  <span className="shrink-0 whitespace-nowrap rounded-[4px] bg-[#EEF0E3] px-2 py-1 text-xs font-semibold text-[#28543D]">
                    {curriculum.lesson_count} lessons
                  </span>
                </div>
                <div className="mt-6 flex gap-3 text-xs text-[#687368]">
                  <span>{curriculum.layer_count} layers</span>
                  <span>{curriculum.placeholder_count} placeholders</span>
                </div>
              </button>
            ))}
          </div>
        )}
        <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)}>
          <div className="flex flex-col gap-4 p-2">
            <h2 className="text-xl font-bold text-[#294B39]">Add curriculum</h2>
            <label htmlFor="curriculum-name">Name</label>
            <input
              id="curriculum-name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="rounded-lg border border-[#DED8CC] px-3 py-2"
              placeholder="Grade 5 Math"
            />
            <PrimaryButton
              onClick={createCurriculum}
              disabled={!name.trim() || saving || readOnly}
            >
              {saving ? "Creating..." : "Create curriculum"}
            </PrimaryButton>
          </div>
        </Modal>
      </div>
    );
  }

  const maxStructuralDepth = layers.length - 2;
  const currentOrgId = profile?.organization_id ?? null;
  const boundToCurrentOrg = currentOrgId
    ? detail?.organizations.some((org) => org.id === currentOrgId)
    : false;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <button
            className="text-sm text-[#204833] underline"
            onClick={() => navigate("/curriculum")}
          >
            ← All curricula
          </button>
          <h1 className="mt-2 font-libre-baskerville text-[32px] font-normal leading-[1.45] text-[#294B39]">
            {detail?.name}
          </h1>
        </div>
        <div className="flex gap-3">
          <SecondaryButton onClick={() => navigate("/curriculum")}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={() => void save()}
            disabled={readOnly || saving || !name.trim()}
          >
            <Save className="mr-2 inline h-4 w-4" />
            {saving ? "Saving..." : "Save"}
          </PrimaryButton>
        </div>
      </div>
      {locked && (
        <div
          role="status"
          className="rounded-[6px] bg-amber-50 p-4 text-amber-900"
        >
          Trial locked. You can review curriculum structure, but cannot save
          changes.
        </div>
      )}
      {!curriculumManagementEnabled && (
        <div
          role="status"
          className="rounded-[6px] border border-[#DED8CC] bg-[#EDE8DC] p-4 text-[#294B39]"
        >
          Curriculum authoring is disabled for this workspace. This page is
          read-only.
        </div>
      )}
      <Modal
        isOpen={pendingRemoval !== null}
        onClose={() => setPendingRemoval(null)}
      >
        <div className="flex flex-col gap-4 p-2">
          <h2 className="text-xl font-bold text-[#294B39]">
            Confirm lesson removals
          </h2>
          <p className="text-sm text-[#687368]">
            Saving removes these lessons from the curriculum. Any student
            already scheduled on one loses it.
          </p>
          <ul className="flex max-h-64 flex-col gap-2 overflow-y-auto">
            {(pendingRemoval ?? []).map((node) => (
              <li
                key={node.id}
                className="rounded-lg border border-[#DED8CC] px-3 py-2 text-sm"
              >
                <span className="font-semibold text-[#294B39]">
                  {node.name ?? `Lesson ${node.id}`}
                </span>
                <span className="ml-2 text-xs text-[#687368]">
                  {node.scheduled
                    ? "assigned to students"
                    : "published, not yet assigned"}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setPendingRemoval(null)}
              className="rounded-lg border border-[#DED8CC] px-4 py-2 text-sm font-semibold text-[#294B39]"
            >
              Keep them
            </button>
            <PrimaryButton
              onClick={() =>
                void save((pendingRemoval ?? []).map((node) => node.id))
              }
              disabled={saving}
            >
              {saving ? "Saving..." : "Remove and save"}
            </PrimaryButton>
          </div>
        </div>
      </Modal>
      {error && (
        <div role="alert" className="rounded-[6px] bg-red-50 p-4 text-red-800">
          {error}
        </div>
      )}
      <section className="rounded-[8px] border border-[#DED8CC] bg-white p-5 shadow-sm">
        <label className="text-sm font-semibold" htmlFor="detail-name">
          Curriculum name
        </label>
        <input
          id="detail-name"
          disabled={readOnly}
          value={name}
          onChange={(event) => setName(event.target.value)}
          className="mt-2 w-full rounded-lg border border-[#DED8CC] px-3 py-2 focus:ring-2 focus:ring-[#28543D]"
        />
      </section>
      <section className="rounded-[8px] border border-[#DED8CC] bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-[#294B39]">
              Curriculum structure
            </h2>
            <p className="text-sm text-[#687368]">
              Grade is always first and Lesson is always last.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#687368]">
              {layers.length} layers
            </span>
            {layers.length < 5 && (
              <button
                disabled={readOnly}
                onClick={() =>
                  setLayers((current) =>
                    [
                      ...current.slice(0, -1),
                      { depth: current.length - 1, name: "Section" },
                      current[current.length - 1],
                    ].map((layer, index) => ({ ...layer, depth: index })),
                  )
                }
                className="rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-[#28543D]"
              >
                + Layer
              </button>
            )}
            {layers.length > 2 && (
              <button
                disabled={readOnly}
                onClick={() =>
                  setLayers((current) =>
                    current
                      .filter((_, index) => index !== current.length - 2)
                      .map((layer, index) => ({ ...layer, depth: index })),
                  )
                }
                className="rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-[#28543D]"
              >
                − Layer
              </button>
            )}
          </div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {layers.map((layer, index) => (
            <input
              key={layer.depth}
              disabled={readOnly || index === 0 || index === layers.length - 1}
              value={layer.name}
              onChange={(event) =>
                setLayers((current) =>
                  current.map((item) =>
                    item.depth === layer.depth
                      ? { ...item, name: event.target.value }
                      : item,
                  ),
                )
              }
              aria-label={`Layer ${index + 1} name`}
              className="rounded-lg border border-[#DED8CC] px-3 py-2"
            />
          ))}
        </div>
      </section>
      <section className="rounded-[8px] border border-[#DED8CC] bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-[#294B39]">Hierarchy</h2>
            <p className="text-sm text-[#687368]">
              Use placeholders until published lessons are ready.
            </p>
          </div>
          <button
            disabled={readOnly}
            onClick={addGrade}
            className="inline-flex items-center rounded-lg border border-[#28543D] px-3 py-2 text-sm font-semibold text-[#204833] focus:ring-2 focus:ring-[#28543D]"
          >
            <Plus className="mr-1 h-4 w-4" /> Add grade
          </button>
        </div>
        <div className="mt-4 flex flex-col gap-3">
          {nodes.length === 0 ? (
            <div className="rounded-[6px] border border-dashed p-8 text-center text-[#687368]">
              No grades yet. Add a grade to start.
            </div>
          ) : (
            nodes.map((node) => (
              <NodeEditor
                key={node.id}
                node={node}
                depth={0}
                maxStructuralDepth={maxStructuralDepth}
                readOnly={readOnly}
                lessons={lessons}
                gradeOptions={gradeOptions}
                onAddChild={addChild}
                onAddLesson={addLesson}
                onUpdate={(id, updater) =>
                  setNodes((current) => updateNode(current, id, updater))
                }
                onRemove={(id) =>
                  setNodes((current) => removeNode(current, id))
                }
              />
            ))
          )}
        </div>
      </section>
      <section className="rounded-[8px] border border-[#DED8CC] bg-white p-5 shadow-sm">
        <h2 className="text-lg font-bold text-[#294B39]">
          Organization binding
        </h2>
        <p className="mt-1 text-sm text-[#687368]">
          This toggles your own organization. Other organizations bound to this
          curriculum are managed from the internal dashboard and are left as
          they are.
        </p>
        <label className="mt-4 flex items-center gap-3 text-sm">
          <input
            type="checkbox"
            disabled={readOnly || !currentOrgId}
            checked={Boolean(boundToCurrentOrg)}
            onChange={async (event) => {
              if (!detail || !currentOrgId) return;
              try {
                const response = await api.put<CurriculumDetail>(
                  `/curriculum/${detail.id}/organizations`,
                  {
                    // The endpoint takes the complete desired set, but this
                    // checkbox only edits the caller's own organization —
                    // sending just that one would unassign every other
                    // organization bound to this curriculum.
                    organization_ids: (() => {
                      const others = detail.organizations
                        .map((organization) => organization.id)
                        .filter((id) => id !== currentOrgId);
                      return event.target.checked
                        ? [...others, currentOrgId]
                        : others;
                    })(),
                  },
                );
                setDetail(response.data);
                setSelectedOrg(response.data.organizations[0]?.id ?? null);
              } catch {
                setError("Unable to update organization binding.");
              }
            }}
          />{" "}
          Bind to current organization{currentOrgId ? ` (${currentOrgId})` : ""}
        </label>
        <div className="mt-3 text-sm text-[#294B39]">
          {selectedOrg
            ? `Bound organization: ${selectedOrg}`
            : "Not bound to an organization"}
        </div>
      </section>
      <section className="rounded-[8px] border border-[#DED8CC] bg-white p-5 shadow-sm">
        <h2 className="text-lg font-bold text-[#294B39]">Curriculum logo</h2>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          {detail?.logo_url ? (
            <img
              src={detail.logo_url}
              alt="Curriculum logo"
              className="h-16 w-16 rounded-[6px] object-cover"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-[6px] bg-[#EEF0E3] text-[#204833]">
              <ImagePlus />
            </div>
          )}
          <label className="cursor-pointer rounded-lg border px-3 py-2 text-sm font-semibold focus-within:ring-2 focus-within:ring-[#28543D]">
            Upload logo
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              disabled={readOnly}
              className="sr-only"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (!file || !detail || !currentOrgId) return;
                try {
                  const buffer = await file.arrayBuffer();
                  const digest = await crypto.subtle.digest("SHA-256", buffer);
                  const checksum = Array.from(new Uint8Array(digest))
                    .map((byte) => byte.toString(16).padStart(2, "0"))
                    .join("");
                  const presign = await api.post(
                    `/curriculum/${detail.id}/logo/presign`,
                    { content_type: file.type, size: file.size, checksum },
                  );
                  await fetch(presign.data.upload_url, {
                    method: "PUT",
                    headers: presign.data.headers,
                    body: file,
                  });
                  const response = await api.post<CurriculumDetail>(
                    `/curriculum/${detail.id}/logo/complete`,
                    { object_key: presign.data.object_key, checksum },
                  );
                  setDetail(response.data);
                } catch {
                  setError("Unable to upload curriculum logo.");
                }
              }}
            />
          </label>
          {detail?.logo_url && (
            <button
              disabled={readOnly}
              className="text-sm text-red-700 underline"
              onClick={async () => {
                if (!detail) return;
                try {
                  const response = await api.delete<CurriculumDetail>(
                    `/curriculum/${detail.id}/logo`,
                  );
                  setDetail(response.data);
                } catch {
                  setError("Unable to remove curriculum logo.");
                }
              }}
            >
              Remove logo
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

const NodeEditor = ({
  node,
  depth,
  maxStructuralDepth,
  readOnly,
  lessons,
  gradeOptions,
  onAddChild,
  onAddLesson,
  onUpdate,
  onRemove,
}: {
  node: EditorNode;
  depth: number;
  maxStructuralDepth: number;
  readOnly: boolean;
  lessons: LessonOption[];
  gradeOptions: GradeOption[];
  onAddChild: (node: EditorNode) => void;
  onAddLesson: (node: EditorNode) => void;
  onUpdate: (id: number, updater: (node: EditorNode) => EditorNode) => void;
  onRemove: (id: number) => void;
}) => (
  <div
    className="rounded-[6px] border border-[#DED8CC] p-3"
    style={{ marginLeft: `${depth * 1.25}rem` }}
  >
    <div className="flex flex-wrap items-center gap-2">
      <button
        aria-label={`Remove ${node.name}`}
        disabled={readOnly}
        onClick={() => onRemove(node.id)}
        className="rounded p-1 text-red-600 focus:ring-2 focus:ring-[#28543D]"
      >
        <Trash2 className="h-4 w-4" />
      </button>
      {node.children.length ? (
        <ChevronDown className="h-4 w-4 text-[#687368]" />
      ) : (
        <ChevronRight className="h-4 w-4 text-[#687368]" />
      )}
      {depth === 0 && gradeOptions.length > 0 ? (
        <select
          disabled={readOnly}
          value={node.name}
          onChange={(event) =>
            onUpdate(node.id, (current) => ({
              ...current,
              name: event.target.value,
            }))
          }
          aria-label={`${node.name} grade`}
          className="min-w-[180px] flex-1 rounded-lg border border-[#DED8CC] px-3 py-2"
        >
          {/* A grade authored before this vocabulary was enforced keeps its
              name: it is listed so it round-trips unchanged instead of
              silently becoming another grade, and labelled so the author can
              see why their students are not being placed. */}
          {!gradeOptions.some((option) => option.label === node.name) && (
            <option value={node.name}>{node.name} — not a grade</option>
          )}
          {gradeOptions.map((option) => (
            <option key={option.code} value={option.label}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          disabled={readOnly}
          value={node.name}
          onChange={(event) =>
            onUpdate(node.id, (current) => ({
              ...current,
              name: event.target.value,
            }))
          }
          aria-label={`${node.name} name`}
          className="min-w-[180px] flex-1 rounded-lg border border-[#DED8CC] px-3 py-2"
        />
      )}
      {depth < maxStructuralDepth && (
        <button
          disabled={readOnly}
          onClick={() => onAddChild(node)}
          className="rounded-lg border px-2 py-1 text-xs focus:ring-2 focus:ring-[#28543D]"
        >
          + Section
        </button>
      )}
      {depth === maxStructuralDepth && (
        <button
          disabled={readOnly}
          onClick={() => onAddLesson(node)}
          className="rounded-lg border px-2 py-1 text-xs focus:ring-2 focus:ring-[#28543D]"
        >
          + Lesson
        </button>
      )}
    </div>
    <div className="mt-3 flex flex-col gap-2">
      {node.lessons.map((lesson) => (
        <div key={lesson.id} className="ml-8 flex flex-wrap items-center gap-2">
          <input
            // A trashed slot is left out of the save, so a rename would be lost.
            disabled={readOnly || lesson.trashed}
            value={lesson.title}
            onChange={(event) =>
              onUpdate(node.id, (current) => ({
                ...current,
                lessons: current.lessons.map((item) =>
                  item.id === lesson.id
                    ? { ...item, title: event.target.value }
                    : item,
                ),
              }))
            }
            aria-label="Lesson slot title"
            className="min-w-[160px] flex-1 rounded-lg border border-[#DED8CC] px-3 py-2 text-sm"
          />
          <select
            disabled={readOnly}
            value={lesson.lesson_id ?? ""}
            onChange={(event) =>
              onUpdate(node.id, (current) => ({
                ...current,
                lessons: current.lessons.map((item) =>
                  item.id === lesson.id
                    ? {
                        ...item,
                        lesson_id: event.target.value
                          ? Number(event.target.value)
                          : null,
                        // A choice made here is sent: a lesson is a swap,
                        // and Placeholder empties the slot as it always has.
                        trashed: false,
                      }
                    : item,
                ),
              }))
            }
            aria-label="Published lesson"
            className="min-w-[180px] rounded-lg border border-[#DED8CC] px-3 py-2 text-sm"
          >
            <option value="">Placeholder</option>
            {lessons.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
          <button
            aria-label={
              trashAction(lesson) === "delete"
                ? `Remove ${lesson.title}`
                : `Empty ${lesson.title}`
            }
            title={TRASH_HINTS[trashAction(lesson)]}
            disabled={readOnly || trashAction(lesson) === "kept"}
            onClick={() =>
              onUpdate(node.id, (current) => ({
                ...current,
                lessons: trashLessonSlot(current.lessons, lesson.id),
              }))
            }
            className="rounded p-1 text-red-600 focus:ring-2 focus:ring-[#28543D] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      {node.children.map((child) => (
        <NodeEditor
          key={child.id}
          node={child}
          depth={depth + 1}
          maxStructuralDepth={maxStructuralDepth}
          readOnly={readOnly}
          lessons={lessons}
          gradeOptions={gradeOptions}
          onAddChild={onAddChild}
          onAddLesson={onAddLesson}
          onUpdate={onUpdate}
          onRemove={onRemove}
        />
      ))}
    </div>
  </div>
);

export default CurriculumManagementPage;
